/**
 * FRONT-2594 — pure diff behind Canvas auto-open.
 *
 * The caller holds a BASELINE (`null` until the chat's history has finished
 * loading and been seeded into the message list). While the baseline is null
 * nothing is ever "changed": the first call after history is ready ADOPTS the
 * current items as the baseline. History arriving late therefore never counts
 * as new — it is the baseline, not a change.
 *
 * The baseline only ever GROWS within one chat branch: every key stays in it
 * with the SET of tool-call ids and the highest updatedAt seen. An item that
 * briefly drops out of the message list (a running turn's strip-and-replay on
 * resume, a retry that cuts the list back) and then comes back is therefore not
 * "new" again. A change is an id never seen before for that item — not a count
 * of outputs, which a deleted message would lower so that the next write lands
 * on a number already seen. The caller starts a fresh baseline when the chat or
 * its branch changes.
 */
import type { CanvasItem } from './collect-items';

export interface SeenItem {
  /** Every tool-call id seen for this item (grow-only). */
  callIds: string[];
  updatedAt: string | null;
}

export interface CanvasBaseline {
  /** key -> tool-call ids and highest updatedAt seen so far. */
  seen: Record<string, SeenItem>;
}

export interface AutoOpenResult {
  baseline: CanvasBaseline;
  /** Keys newly created or changed since the baseline, in item order. */
  changedKeys: string[];
}

/**
 * FRONT-2669: only an EXPLICIT output can make an item "ahead". Every output
 * still grows the baseline (see `growBaseline`), so a titled `file_write` or a
 * Markdown output file is remembered as seen without ever opening the panel or
 * showing the unseen dot.
 */
function isAhead(item: CanvasItem, seen: SeenItem | undefined): boolean {
  if (item.explicitCallIds.length === 0) return false;
  if (!seen) return true;
  if (item.explicitCallIds.some((id) => !seen.callIds.includes(id))) return true;
  if (!item.latestExplicit || item.updatedAt === null) return false;
  if (seen.updatedAt === null) return true;
  return item.updatedAt > seen.updatedAt;
}

/** The baseline with every item folded in, keeping the highest value per key. */
export function growBaseline(baseline: CanvasBaseline | null, items: readonly CanvasItem[]): CanvasBaseline {
  const seen: Record<string, SeenItem> = { ...(baseline?.seen ?? {}) };
  for (const item of items) {
    const prev = seen[item.key];
    seen[item.key] = {
      callIds: [...new Set([...(prev?.callIds ?? []), ...item.callIds])],
      updatedAt:
        item.updatedAt !== null && (prev?.updatedAt == null || item.updatedAt > prev.updatedAt)
          ? item.updatedAt
          : (prev?.updatedAt ?? null),
    };
  }
  return { seen };
}

export function toBaseline(items: readonly CanvasItem[]): CanvasBaseline {
  return growBaseline(null, items);
}

/**
 * @param baseline previous baseline, or null while history is not yet loaded/seeded
 * @param historyReady true only once history has loaded AND reached the message list
 */
export function diffCanvasItems(
  baseline: CanvasBaseline | null,
  items: readonly CanvasItem[],
  historyReady: boolean,
): AutoOpenResult {
  if (!historyReady) return { baseline: baseline ?? { seen: {} }, changedKeys: [] };
  if (!baseline) return { baseline: toBaseline(items), changedKeys: [] };
  const changedKeys = items.filter((i) => isAhead(i, baseline.seen[i.key])).map((i) => i.key);
  return { baseline: growBaseline(baseline, items), changedKeys };
}

export type AutoOpenAction =
  | { kind: 'none' }
  /** Wide screen: open the panel on this item (the changed one produced most recently). */
  | { kind: 'open'; key: string }
  /** Narrow screen, or an unsaved draft is on screen: leave the view alone, only mark it. */
  | { kind: 'unseen' };

/**
 * What the chat window does for one observation of (items, history state).
 * Unlike `diffCanvasItems`, a not-ready observation returns the baseline
 * UNCHANGED (still null): the caller stores the result as-is, so there is no
 * way to adopt an empty baseline before history is seeded — the failure that
 * would make a reopened old chat auto-open.
 *
 * `lockActive`: a document with an unsaved draft is on screen, so a change must
 * never switch the active item away from it — it is only marked.
 */
export function planAutoOpen(
  baseline: CanvasBaseline | null,
  items: readonly CanvasItem[],
  historyReady: boolean,
  wide: boolean,
  lockActive = false,
): { baseline: CanvasBaseline | null; action: AutoOpenAction } {
  if (!historyReady) return { baseline, action: { kind: 'none' } };
  const result = diffCanvasItems(baseline, items, true);
  if (result.changedKeys.length === 0) return { baseline: result.baseline, action: { kind: 'none' } };
  if (!wide || lockActive) return { baseline: result.baseline, action: { kind: 'unseen' } };
  // The newest = the changed item whose latest output sits latest in the messages.
  const changed = items.filter((i) => result.changedKeys.includes(i.key));
  const newest = changed.reduce((best, i) => (i.seq > best.seq ? i : best));
  return { baseline: result.baseline, action: { kind: 'open', key: newest.key } };
}

/**
 * The baseline belongs to one (chat, branch) scope. Moving to another scope
 * discards it (the next ready observation adopts a fresh one); staying in the
 * same scope keeps it. The hook calls this on every observation, so branch
 * A -> B -> A starts three baselines and never reports the replayed items.
 */
export function baselineForScope(
  held: { scope: string; baseline: CanvasBaseline | null },
  scope: string,
): { scope: string; baseline: CanvasBaseline | null; reset: boolean } {
  return held.scope === scope
    ? { scope, baseline: held.baseline, reset: false }
    : { scope, baseline: null, reset: true };
}
